import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Alert, Grid, Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PlanDetailInfoCard from "../../../../components/Cards/PlanDetailInfoCard";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import NavigationDrawer from "../../../../components/NavigationDrawer";
import PlainDetailInfoSkeleton from "../../../../components/Skeleton/PlainDetailInfoSkeleton";
import {
    CURRENCY,
    DEFAULT_VAT_PERCENTAGE,
} from "../../../../constants/Variables";
import { getMatchingDiscountRule } from "../../../../services/discountRuleApiFn";
import {
    getPlanById,
    getPlanCreditSuggestion,
} from "../../../../services/planApiFn";
import { calculateProductPricing } from "../../../../utils";
import PlanIncludedItemsTable from "../../../../components/Tables/PlanIncludedItemsTable";

const tableHeader = [
    { label: "itemType", id: "item_type" },
    { label: "itemName", id: "name" },
    { label: "itemPrice", id: "price" },
    { label: "quantity", id: "quantity" },
    { label: "availabliity", id: "item_availability" },
    { label: "itemStatus", id: "item_status" },
];

const tableRows = [
    "item_type",
    "name_image",
    "item_price",
    "item_qty",
    "item_availability",
    "item_status",
];

export const PlanDetail = ({ }) => {
    // Navigate to the previous page
    const navigate = useNavigate();
    // Get the plan id from the url
    const { id } = useParams();
    // Get the plan detail from the api
    const { t } = useTranslation(["products", "common"]);
    // Set the tax value state
    const [planDetails, setPlanDetails] = useState(null);
    // Set the loading state
    const [isLoading, setIsLoading] = useState(false);
    // Set the matching discount rule state
    const [matchingDiscountRule, setMatchingDiscountRule] = useState(null);
    // Set the plan credit suggestion state
    const [planCreditSuggestion, setPlanCreditSuggestion] = useState(null);
    // Get Setting from the redux store
    const settings = useSelector((state) => state.settingsReducer.settings);
    // Set the tax value
    const taxValue = settings?.tax || DEFAULT_VAT_PERCENTAGE || 21;
    // Get the current language from the redux store
    const currentLanguage = useSelector(
        (state) => state.languageReducer.currentLanguage,
    );

    // Fetch the plan pricing insights
    const fetchPlanPricingInsights = async (amount) => {
        try {
            const [discountRuleResponse, creditSuggestionResponse] =
                await Promise.all([
                    getMatchingDiscountRule({ amount }),
                    getPlanCreditSuggestion({ amount }),
                ]);

            if (discountRuleResponse.data.status === "success") {
                setMatchingDiscountRule(discountRuleResponse.data.data);
            } else {
                console.error(discountRuleResponse.data.message);
            }

            if (creditSuggestionResponse.data.status === "success") {
                setPlanCreditSuggestion(creditSuggestionResponse.data.data);
            } else {
                console.error(creditSuggestionResponse.data.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Fetch the plan details
    const fetchPlanDetails = async (planId) => {
        try {
            setIsLoading(true);
            const response = await getPlanById(planId);
            if (response.data.status === "success") {
                const data = response.data.data;
                setPlanDetails(data);

                const calculatedPricing = calculateProductPricing(
                    data?.planDiscountEffectivePrice,
                    0,
                    taxValue,
                );

                fetchPlanPricingInsights(calculatedPricing?.productPriceWithVat);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch the plan details when the component mounts
    useEffect(() => {
        if (id) {
            fetchPlanDetails(id);
        }
    }, [id]);

    // Calculate the total original amount
    const totalOrignalAmount = calculateProductPricing(
        planDetails?.planTotalPrice,
        0,
        taxValue,
    );

    // Calculate the next delivery diff days
    const nextDeliveryDiffDays = planDetails?.nextDeliveryAt
        ? moment(planDetails?.nextDeliveryAt)
            .startOf("day")
            .diff(moment().startOf("day"), "days")
        : null;

    // Render the plan detail data
    const PLAN_DETAIL_DATA = [
        {
            icon: <CreditCardIcon fontSize="large" />,
            title: t("freeCredit", { ns: "common" }),
            value: `${matchingDiscountRule?.discountAmount || 0} ${CURRENCY}`,
        },
        {
            icon: <AccountBalanceWalletIcon fontSize="large" />,
            title: t("totalCost", { ns: "common" }),
            value: `${totalOrignalAmount?.productPriceWithVat || 0} ${CURRENCY}`,
            subValue: t("perDelivery", { ns: "common" }),
        },
        {
            icon: <CalendarMonthIcon fontSize="large" />,
            title: t("nextDelivery", { ns: "common" }),
            value: planDetails?.nextDeliveryAt
                ? moment(planDetails?.nextDeliveryAt).format("DD MMM YYYY")
                : t("notSheduled", { ns: "common" }),
            days: planDetails?.nextDeliveryAt
                ? nextDeliveryDiffDays > 0
                    ? `${t("in", { ns: "common" })} ${nextDeliveryDiffDays} ${t("days", { ns: "common" })}`
                    : nextDeliveryDiffDays === 0
                        ? t("today", { ns: "common" })
                        : t("overdue", { ns: "common" })
                : t("notSheduled", { ns: "common" }),
        },
        {
            icon: <AssignmentIcon fontSize="large" />,
            title: t("totalItems", { ns: "common" }),
            value: `${planDetails?.planTotalItems || 0} ${t("items", { ns: "common" })}`,
        },
        {
            icon: <AssignmentIcon fontSize="large" />,
            title: t("receivedItems", { ns: "common" }),
            value: `${planDetails?.planTotalActiveItems || 0} ${t("items", { ns: "common" })}`,
        },
    ];

    const renderInfoCard = () => {
        if (isLoading) {
            return <PlainDetailInfoSkeleton />;
        }
        return (
            <Stack
                gap={2}
                bgcolor="background.paper"
                p={2}
                borderRadius={2}
                border="1px solid"
                borderColor="divider"
            >
                <Grid container spacing={1}>
                    {PLAN_DETAIL_DATA.map((card, index) => (
                        <Grid item xs={12} sm={6} md={2.4} key={index}>
                            <PlanDetailInfoCard {...card} />
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        );
    };

    // Render the credit suggestion
    const renderCreditSuggestion = () => {
        if (!planCreditSuggestion) return null;
        const suggestionMessage =
            planCreditSuggestion?.message[currentLanguage]?.trim();
        if (!suggestionMessage) return null;

        return (
            <Alert
                severity="success"
                variant="filled"
                sx={{ borderRadius: 2, alignItems: "center" }}
            >
                {suggestionMessage}
            </Alert>
        );
    };

    // Render the included items
    const renderIncludedItems = () => {
        return (
            <React.Fragment>
                <PlanIncludedItemsTable
                    loading={isLoading}
                    tableHeader={tableHeader}
                    tableData={planDetails?.items || []}
                    displayRows={tableRows}
                    // handleItemStatusChange={handleItemStatusChange}
                    // handleItemRemove={handleItemRemove}
                    // handleUpdateItemQuantity={handleUpdateItemQuantity}
                    showPagination={true}
                />
                {/* <Stack>
                    <GlobalButton
                        btnText="addItem"
                        handleClickBtn={() => openAddItemToPlanDialog.current.openDialog({
                            planId: planId,
                        })}

                        startIcon={<AddIcon />}
                    />
                </Stack> */}
            </React.Fragment>
        );
    };

    const renderAlerts = () => {
        const isValidityExpired =
            Boolean(planDetails?.validityPeriod) &&
            moment(planDetails.validityPeriod)
                .startOf("day")
                .isBefore(moment().startOf("day"));
        const isPlanPaused = planDetails?.status === "paused";
        const isPlanInactive =
            planDetails?.status === "draft" || planDetails?.status === "inactive";

        return (
            <React.Fragment>
                 <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        {t("discountProductWillNotBeApplied")}
                    </Alert>
                {isValidityExpired && (
                    <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                        {t("validityPeriodExpiredAlert")}
                    </Alert>
                )}
                {isPlanPaused && (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        {t("planPausedAlert")}
                    </Alert>
                )}
                {isPlanInactive && (
                    <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                        {t("planInactiveAlert")}
                    </Alert>
                )}
            </React.Fragment>
        );
    };

    // Render the plan detail page
    return (
        <NavigationDrawer>
            <PaperContainer
                title={t("planDetail")}
                subtitle={t("planDetailDescription")}
                onBack={() => navigate(-1)}
                showBackButton={true}
            >
                <Stack gap={2}>
                    {renderAlerts()}
                    {renderInfoCard()}
                    {renderCreditSuggestion()}
                    {renderIncludedItems()}
                    {/*
                    {renderPricingSummary()} */}
                </Stack>
            </PaperContainer>
        </NavigationDrawer>
    );
};
